package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.shared.CreateScreenshotReviewLinkRequest
import dev.lingvo.shared.RenderDto
import dev.lingvo.shared.ScreenshotReviewLinkDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.review.publicReviewLink
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.browser.window
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

/** Admins share only explicit current-version renders. Link metadata never recovers a secret. */
@Composable
internal fun ScreenshotReviewLinks(
    slug: String, platform: StorePlatform, version: Int, renders: List<RenderDto>, canManage: Boolean,
) {
    val subject = liveProjectRoute(slug)?.authoritySubject(Session.organizations)
    val person = Session.isAuthenticated && Session.token?.split('.')?.size == 3
    if (!canManage || !person || version <= 0) return
    var reload by remember(subject, platform) { mutableStateOf(0) }
    val loaded = loadReloadable(subject, platform, reloadToken = reload) {
        val action = subject?.action ?: awaitCancellation()
        requestProjectResource(action, slug) { ScreenshotReviewApi.links(slug, platform) }
    }
    val scope = rememberCoroutineScope()
    val runner = remember(subject, platform) { ScreenshotsActionRunner(slug, { scope.launch { it() } }) }
    var hours by remember(subject, platform) { mutableStateOf("168") }
    var busy by remember(subject, platform) { mutableStateOf(false) }
    var failure by remember(subject, platform) { mutableStateOf<String?>(null) }
    var createdUrl by remember(subject, platform) { mutableStateOf<String?>(null) }
    val selected = renders.filter { it.designVersion == version }.distinctBy { it.id }
    val control = Modifier.styleModifier { property("min-height", "40px"); property("max-width", "100%"); property("white-space", "normal") }
    Column(Modifier.fillMaxWidth().gap(Space.MD).styleModifier { property("min-width", "0"); property("overflow-wrap", "anywhere") }) {
        H3 { Text(t("app.projects.slug.screenshots.screenshot_review_links.read_only_review_links", "Read-only review links")) }
        LMuted(t("app.projects.slug.screenshots.screenshot_review_links.share_frozen_renders_from_this_saved", "Share frozen renders from this saved design version. Anyone with the link can view its images and captions until it expires or you revoke it."))
        LMuted(tf("screenshots.review_links.selected", "{count} selected images from version {version}. Choose sets above to change the images shared.", "count" to selected.size, "version" to version))
        if (renders.any { it.designVersion != version }) LMuted(t("app.projects.slug.screenshots.screenshot_review_links.compose_the_selected_screenshots_for_the", "Compose the selected screenshots for the current saved version before sharing them."))
        LSelect(hours, listOf(LSelectOption("1", t("app.projects.slug.screenshots.screenshot_review_links.1_hour", "1 hour")),
            LSelectOption("24", t("app.projects.slug.screenshots.screenshot_review_links.24_hours", "24 hours")), LSelectOption("168", t("app.projects.slug.screenshots.screenshot_review_links.7_days", "7 days"))),
            { hours = it }, label = t("app.projects.slug.screenshots.screenshot_review_links.link_expires_after", "Link expires after"), enabled = !busy, modifier = Modifier.fillMaxWidth())
        LButton(t("app.projects.slug.screenshots.screenshot_review_links.create_read_only_review_link", "Create read-only review link"), {
            createdUrl = null
            runner.run(ScreenshotsPrivilege.APPROVE, { busy = it }, {
                ScreenshotReviewApi.createLink(slug, platform, CreateScreenshotReviewLinkRequest(version, selected.map { it.id }, hours.toInt()))
            }, { failure = tr("app.projects.slug.screenshots.screenshot_review_links.the_review_link_could_not_be", "The review link could not be changed. Refresh and retry.") }, { result ->
                failure = null
                createdUrl = publicReviewLink(window.location.origin, result.token)
                reload++
            })
        }, enabled = !busy && selected.size in 1..60 && !loaded.refreshing && loaded.refreshError == null,
            loading = busy, variant = LButtonVariant.SECONDARY, modifier = control)
        if (selected.size > 60) LMuted(t("app.projects.slug.screenshots.screenshot_review_links.choose_at_most_60_images_for", "Choose at most 60 images for one review link."))
        createdUrl?.let { value ->
            LAlert(t("app.projects.slug.screenshots.screenshot_review_links.copy_this_link_now_it_is", "Copy this link now. It is shown only once and cannot be recovered later."), variant = LAlertVariant.INFO)
            Label(forId = "created-screenshot-review-link") { Text(t("app.projects.slug.screenshots.screenshot_review_links.new_review_link", "New review link")) }
            TextArea(value, attrs = {
                id("created-screenshot-review-link"); attr("readonly", ""); attr("spellcheck", "false"); attr("autocomplete", "off")
                style { property("width", "100%"); property("min-height", "80px"); property("box-sizing", "border-box"); property("overflow-wrap", "anywhere") }
            })
            LButton(t("app.projects.slug.screenshots.screenshot_review_links.hide_this_link", "Hide this link"), { createdUrl = null }, variant = LButtonVariant.GHOST, modifier = control)
        }
        failure?.let { LAlert(it, variant = LAlertVariant.WARNING, live = true) }
        LButton(t("app.projects.slug.screenshots.screenshot_review_links.refresh_review_links", "Refresh review links"), { createdUrl = null; reload++ }, enabled = !busy, variant = LButtonVariant.GHOST, modifier = control)
        loaded.refreshError?.let { AsyncFailureState(it, subject = t("app.projects.slug.screenshots.screenshot_review_links.the_review_links", "the review links"), onRetry = { reload++ }) }
        when (val state = loaded.state) {
            Async.Loading -> LoadingState(rows = 1)
            is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.screenshot_review_links.the_review_links", "the review links"), onRetry = { reload++ })
            is Async.Success -> ReviewLinkList(state.value, !busy && !loaded.refreshing && loaded.refreshError == null) { link ->
                createdUrl = null
                runner.run(ScreenshotsPrivilege.APPROVE, { busy = it }, { ScreenshotReviewApi.revokeLink(slug, platform, link.id) },
                    { failure = tr("app.projects.slug.screenshots.screenshot_review_links.the_review_link_could_not_be", "The review link could not be changed. Refresh and retry.") }, { failure = null; reload++ })
            }
        }
    }
}

/** Only safe metadata survives page reload, with no token, bearer prefix or public URL. */
@Composable
internal fun ReviewLinkList(links: List<ScreenshotReviewLinkDto>, enabled: Boolean, onRevoke: (ScreenshotReviewLinkDto) -> Unit) {
    if (links.isEmpty()) LMuted(t("app.projects.slug.screenshots.screenshot_review_links.no_review_links_yet", "No review links yet."))
    links.forEach { link ->
        Div(attrs = { attr("data-review-link", ""); style { property("overflow-wrap", "anywhere"); property("margin-block", "12px") } }) {
            LMuted(tf("screenshots.review_links.metadata", "Version {version} · {count} images · expires {expiry}",
                "version" to link.designVersion, "count" to link.imageCount, "expiry" to link.expiresAt))
            if (link.revokedAt != null) LMuted(t("app.projects.slug.screenshots.screenshot_review_links.revoked", "Revoked"))
            else LButton(t("app.projects.slug.screenshots.screenshot_review_links.revoke_review_link", "Revoke review link"), { onRevoke(link) }, enabled = enabled,
                accessibleLabel = tf("screenshots.review_links.revoke_label", "Revoke version {version} review link expiring {expiry}", "version" to link.designVersion, "expiry" to link.expiresAt),
                variant = LButtonVariant.DANGER, modifier = Modifier.styleModifier { property("min-height", "40px"); property("white-space", "normal"); property("max-width", "100%") })
        }
    }
}
